import { useEffect, useState } from "react";
import { collection, onSnapshot, type Firestore } from "firebase/firestore";
import { useLegacyCustomerCards } from "./useLegacyCustomerCards";

export type CustomerCard = {
  id: string; name: string; total: number | null; completed: number | null; orderCount: number | null;
  statsStatus?: "loading" | "ready" | "missing" | "stale";
};

export function validStats(data: any): boolean {
  return data?.schemaVersion === 1 && [data.total, data.completed, data.orderCount]
    .every(value => Number.isSafeInteger(value) && value >= 0) && data.completed <= data.total;
}

export function useMaterializedCustomerCards(db: Firestore, opts?: { enabled?: boolean }) {
  const [cards, setCards] = useState<CustomerCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (opts?.enabled === false) return;
    let active = true;
    let customers: { id: string; name: string }[] | null = null;
    const stats = new Map<string, any>();
    let statsLoaded = false;
    let statsFromCache = true;
    const failures = new Map<string, string>();
    setLoading(true);
    setError(null);
    const publish = () => {
      if (!active) return;
      setError(failures.size ? [...failures.values()].join(" · ") : null);
      setLoading(!customers && !failures.has("customers"));
      if (!customers) return;
      setCards(previous => customers!.map(customer => {
        const data = stats.get(customer.id);
        const old = previous.find(card => card.id === customer.id);
        if (failures.has("stats") && old?.total != null) return { ...old, ...customer, statsStatus: "stale" };
        if (validStats(data)) return { ...customer, total: data.total, completed: data.completed,
          orderCount: data.orderCount, statsStatus: failures.has("stats") || statsFromCache ? "stale" : "ready" };
        if (!statsLoaded && old?.total != null) return { ...old, ...customer, statsStatus: "stale" };
        return { ...customer, total: null, completed: null, orderCount: null,
          statsStatus: statsLoaded || failures.has("stats") ? "missing" : "loading" };
      }));
    };
    const fail = (key: string, label: string) => (e: Error) => {
      failures.set(key, `${label}: ${e.message}`);
      publish();
    };
    const stopCustomers = onSnapshot(collection(db, "customers"), snapshot => {
      customers = snapshot.docs.map(doc => ({ id: doc.id, name: typeof doc.data().name === "string" ? doc.data().name : doc.id }));
      failures.delete("customers");
      publish();
    }, fail("customers", "Kundlistan kunde inte uppdateras"));
    const stopStats = onSnapshot(collection(db, "customerStats"), { includeMetadataChanges: true }, snapshot => {
      // An empty cache snapshot is not proof that server statistics are missing.
      if (snapshot.metadata.fromCache && snapshot.empty) return;
      stats.clear();
      snapshot.docs.forEach(doc => stats.set(doc.id, doc.data()));
      statsLoaded = true;
      statsFromCache = snapshot.metadata.fromCache;
      failures.delete("stats");
      publish();
    }, fail("stats", "Statistiken kunde inte uppdateras"));
    return () => { active = false; stopCustomers(); stopStats(); };
  }, [db, opts?.enabled]);
  return { cards, loading, error };
}

// Fixed at build time. Production retains its current behavior until explicitly enabled.
export const useCustomerCards = process.env.REACT_APP_CUSTOMER_STATISTICS_ENABLED === "true"
  ? useMaterializedCustomerCards : useLegacyCustomerCards;
